import {noop} from '@benchling/js-utils';
import React, {useEffect, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import * as PermissionDAO from 'auth/PermissionDAO';
import * as NavConstants from 'constants/NavConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import makeCancelable from 'lib/makeCancelable';
import ExpandedItemTable, {AdditionalColumnData} from 'nav/expanded/ExpandedItemTable';
import {
  ItemListingQuery,
  ItemListingQueryVariables,
} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {ITEM_LISTING_QUERY} from 'nav/sidepanel/listing/ItemListing';
import {
  addPermissionsAndTags,
  idsToItemListingQueryParams,
  queryResultsToItems,
} from 'nav/sidepanel/listing/ItemListingUtil';
import {AsyncFetcherLocation, ListableItem, SearchResults, SearchStatus} from 'search/SearchConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

/**
 * A simplified wrapper over ExpandedItemTable that is decoupled from search functionality.
 * This component can display a list of items in a table format in the same way the search results table does.
 */
export interface ItemDisplayTableProps {
  ids: Array<string>;
  onToggleSelectedIds: (resultIds: Array<string>, isSelected: boolean) => void;
  isCompact: boolean;
  searchSection: NavConstants.Section;
  selectedIds: Array<string>;
  disabledIds?: Array<string>;
  onOpenItem?: (item: ListableItem) => void;
  /* Adds ability to add custom column data to the table */
  additionalColumnData?: Array<AdditionalColumnData>;
}

export const SEARCH_RESULTS_DUMMY: SearchResults = {
  searchId: 'searchId',
  status: SearchStatus.COMPLETE,
  ids: [],
  groupItemIds: [],
  total: 0,
  isExact: true,
  timedOut: false,
  groups: [],
  nextToken: null,
};

export default function ItemDisplayTable(props: ItemDisplayTableProps): JSX.Element {
  const [expandedItemTableGQL, setExpandedItemTableGQL] = useState(false);
  const [expandedItemTableEnhanceProps, setExpandedItemTableEnhanceProps] = useState(false);

  function updateAsyncFetcherIsFetchedState(flagName: AsyncFetcherLocation, value: boolean): void {
    if (flagName === AsyncFetcherLocation.EXPANDED_ITEM_TABLE_GQL) {
      setExpandedItemTableGQL(value);
    } else if (flagName === AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS) {
      setExpandedItemTableEnhanceProps(value);
    }
  }

  const itemsWithPermissions = useItemDisplayData(props.ids);

  // Wait for items to be loaded
  if (itemsWithPermissions == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ExpandedItemTable
      {...props}
      additionalColumnData={props.additionalColumnData}
      onOpenItem={props.onOpenItem ?? noop}
      onToggleSelectedIds={props.onToggleSelectedIds}
      items={itemsWithPermissions}
      actionSource={ActionOrigin.ITEM_DISPLAY_TABLE}
      emptyMessage="No items to display."
      finderId=""
      asyncFetcherIsFetchedState={{
        itemListingEnhanceProps: true,
        expandedItemTableGQL,
        expandedItemTableEnhanceProps,
        sidepanelItemList: true,
      }}
      onUpdateAsyncFetcherIsFetchedState={updateAsyncFetcherIsFetchedState}
      onSelectIds={noop}
      onSetColumnHeaderSort={noop}
      section={null}
      sorts={[]}
      tableKey="item-display-table"
      searchResults={SEARCH_RESULTS_DUMMY}
      onToggleExpandedItem={noop}
      onRequestMoreChildItems={noop}
      childPageSize={25}
    />
  );
}

/**
 * Custom hook to handle fetching and hydrating item data.
 * Separates data fetching logic from presentation logic.
 *
 * @param ids - Array of item IDs to fetch
 * @returns Array of items with permissions, or null if still loading
 */
function useItemDisplayData(ids: Array<string>): Array<ListableItem> | null {
  const [permissionsByItemId, setPermissionsByItemId] = useState<{[id: string]: Permissions} | null>(null);

  const itemListingQueryParams = idsToItemListingQueryParams(ids);
  const itemListingResult = useSkippableBenchlingQuery<ItemListingQuery, ItemListingQueryVariables>(
    ITEM_LISTING_QUERY,
    {variables: itemListingQueryParams}
  );
  const itemsFromQuery =
    itemListingResult.type === QueryResultType.SUCCESS ? queryResultsToItems(itemListingResult.data) : null;

  // Fetch permissions for all items
  useEffect(() => {
    setPermissionsByItemId({});
    const fetchPermissionsPromise = makeCancelable(PermissionDAO.bulkFetchPermissions(ids));

    async function updatePermissions(): Promise<void> {
      try {
        const permissions = await fetchPermissionsPromise.promise;
        setPermissionsByItemId(permissions);
      } catch (e) {
        if (!e.isCanceled) {
          throw e;
        }
      }
    }

    updatePermissions();
    return () => {
      fetchPermissionsPromise.cancel();
    };
  }, [ids]);

  // Return null if data is still loading
  if (itemsFromQuery == null || permissionsByItemId == null) {
    return null;
  }

  return itemsFromQuery.map((item) => addPermissionsAndTags(item, permissionsByItemId));
}
